import { useCallback, useMemo, useState } from 'react'
import { App, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CheckOutlined, CloseOutlined, DollarOutlined, EditOutlined, EyeOutlined, SendOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { formatDate } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { PurchaseDecisionModal, type PurchaseDecisionKind } from './PurchaseDecisionModal'
import { PurchaseDetailDrawer } from './PurchaseDetailDrawer'
import { PurchaseFormModal } from './PurchaseFormModal'
import { PaymentStatusTag, PurchaseStateTag } from './PurchaseTags'
import { SupplierPaymentModal } from './SupplierPaymentModal'
import {
  VERIFIED_STATES,
  fetchPurchase,
  fetchPurchasePage,
  type Purchase,
  type PurchaseFilter,
  type PurchaseRow,
} from './supplierApi'

interface PurchasesTableProps {
  filter: PurchaseFilter
  tableId: string
  hideSupplier?: boolean
}

export function PurchasesTable({ filter, tableId, hideSupplier }: PurchasesTableProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [detailId, setDetailId] = useState<string | null>(null)
  const [editing, setEditing] = useState<Purchase | null>(null)
  const [decision, setDecision] = useState<{ row: PurchaseRow; kind: PurchaseDecisionKind } | null>(null)
  const [paying, setPaying] = useState<PurchaseRow | null>(null)
  const fetchPage = useMemo(() => fetchPurchasePage(filter), [filter])

  const openEditor = useCallback(
    async (row: PurchaseRow) => {
      try {
        setEditing(
          await queryClient.fetchQuery({
            queryKey: queryKeys.supplier.purchase(row.id),
            queryFn: ({ signal }) => fetchPurchase(row.id, signal),
          }),
        )
      } catch (error) {
        void message.error(translateError(error, t))
      }
    },
    [queryClient, message, t],
  )

  const columns = useMemo<ColumnsType<PurchaseRow>>(() => {
    const own = (row: PurchaseRow) => row.createdBy.id === user?.id
    const all: ColumnsType<PurchaseRow> = [
      {
        key: 'number',
        title: t('purchase.field.number'),
        width: 120,
        render: (_, row) => <Typography.Link onClick={() => setDetailId(row.id)}>{row.number}</Typography.Link>,
      },
      { key: 'supplier', title: t('purchase.field.supplier'), width: 200, render: (_, row) => row.supplier.name },
      { key: 'invoiceNo', title: t('purchase.field.invoiceNo'), width: 130, render: (_, row) => row.invoiceNo ?? t('common.dash') },
      { key: 'invoiceDate', title: t('purchase.field.invoiceDate'), width: 120, render: (_, row) => formatDate(row.invoiceDate) },
      { key: 'dueDate', title: t('purchase.field.dueDate'), width: 120, render: (_, row) => formatDate(row.dueDate) },
      { key: 'state', title: t('purchase.field.state'), width: 150, render: (_, row) => <PurchaseStateTag state={row.state} /> },
      { key: 'total', title: t('purchase.field.total'), width: 140, align: 'right', render: (_, row) => formatMoneyValue(row.total) },
      { key: 'paid', title: t('purchase.field.paid'), width: 140, align: 'right', render: (_, row) => formatMoneyValue(row.paid) },
      { key: 'remaining', title: t('purchase.field.remaining'), width: 140, align: 'right', render: (_, row) => formatMoneyValue(row.remaining) },
      { key: 'paymentStatus', title: t('purchase.field.paymentStatus'), width: 150, render: (_, row) => <PaymentStatusTag status={row.paymentStatus} /> },
      { key: 'warehouse', title: t('purchase.field.warehouse'), width: 160, render: (_, row) => row.warehouse?.name ?? t('common.dash') },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              { key: 'open', label: t('purchase.action.open'), icon: <EyeOutlined />, onClick: () => setDetailId(row.id) },
              {
                key: 'edit',
                label: t('purchase.action.edit'),
                icon: <EditOutlined />,
                permission: 'purchase.create',
                hidden: row.state !== 'DRAFT',
                onClick: () => void openEditor(row),
              },
              {
                key: 'submit',
                label: t('purchase.action.submit'),
                icon: <SendOutlined />,
                permission: 'purchase.create',
                hidden: row.state !== 'DRAFT',
                onClick: () => setDecision({ row, kind: 'submit' }),
              },
              {
                key: 'verify',
                label: t('purchase.action.verify'),
                icon: <CheckOutlined />,
                permission: 'purchase.verify',
                hidden: row.state !== 'SUBMITTED' || own(row),
                divider: true,
                onClick: () => setDecision({ row, kind: 'verify' }),
              },
              {
                key: 'selfHint',
                label: t('purchase.selfVerifyHint'),
                permission: 'purchase.verify',
                divider: true,
                disabled: true,
                hidden: row.state !== 'SUBMITTED' || !own(row),
                onClick: () => undefined,
              },
              {
                key: 'pay',
                label: t('purchase.action.pay'),
                icon: <DollarOutlined />,
                permission: 'supplier.pay',
                hidden: !VERIFIED_STATES.includes(row.state) || row.remaining.amount <= 0,
                onClick: () => setPaying(row),
              },
              {
                key: 'cancel',
                label: t('purchase.action.cancel'),
                icon: <CloseOutlined />,
                permission: 'purchase.create',
                danger: true,
                hidden: row.state !== 'DRAFT' && row.state !== 'SUBMITTED',
                onClick: () => setDecision({ row, kind: 'cancel' }),
              },
            ]}
          />
        ),
      },
    ]
    return hideSupplier ? all.filter((column) => column.key !== 'supplier') : all
  }, [t, user?.id, hideSupplier, openEditor])

  return (
    <>
      <DataTable<PurchaseRow>
        queryKey={[...queryKeys.supplier.purchases, filter]}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId={tableId}
        searchable
        searchPlaceholder={t('purchase.search')}
        scrollX={1700}
        stickyFirstColumn
        emptyTitle={t('purchase.empty')}
      />
      <PurchaseDetailDrawer purchaseId={detailId} onClose={() => setDetailId(null)} />
      <PurchaseFormModal open={editing !== null} purchase={editing} onClose={() => setEditing(null)} onSaved={() => undefined} />
      <PurchaseDecisionModal purchase={decision?.row ?? null} decision={decision?.kind ?? 'submit'} onClose={() => setDecision(null)} />
      <SupplierPaymentModal purchase={paying} onClose={() => setPaying(null)} />
    </>
  )
}
